import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const headingFont = { fontFamily: "'Githera Regular', sans-serif" };
const bodyFont = { fontFamily: 'Roboto, sans-serif', fontWeight: 300 };

const stories = [
  {
    id: 1,
    title: 'Improving Living Conditions in the Gobi Region',
    description:
      'Most soums in the Gobi region are located far from central settlements, making it difficult to meet even basic needs for comfortable living. In particular, essential items such as blankets, washing machines, and books are often lacking in school dormitories, local hospitals, and kindergartens. Even during periods of centrally planned social policy, adequate living conditions for people in the Gobi region were not successfully established.',
    image:
      'https://d1f6qhhrbg3j8a.cloudfront.net/img/270653/original/17D_0813.jpg',
    href: '/journal',
  },
  {
    id: 2,
    title: 'Livestock Support Project',
    description:
      "During 2000–2001, repeated droughts and severe winter disasters (dzud) caused the loss of approximately 80% of the total livestock in the Gobi region. This had a devastating impact on the livelihoods of herders. Many households lost all their animals and were left without any source of income, with the number of such families reaching into the dozens.",
    image:
      'https://d1f6qhhrbg3j8a.cloudfront.net/img/270642/original/17D_9970.jpg',
    href: '/journal',
  },
  {
    id: 3,
    title: 'Vaccination Project',
    description:
      'Before 1990, all livestock were owned by socialist cooperatives, and animal health protection was managed by the state. After the collapse of socialism, the newly established democratic governments neglected the health and hygiene of livestock that had been privatized and transferred to herders.As a result, access to veterinary services declined, and due to herders’ limited financial capacity and knowledge, the risk of livestock diseases increased significantly. This became a serious threat to the overall quality of Mongolia’s livestock.\n\nFor the first time in the past 25 years, with the participation of our collaborators and partners, we have been providing five types of veterinary services—including vaccination, washing, and sanitation—to small livestock in the region free of charge since 2015.',
    image:
      'https://d1f6qhhrbg3j8a.cloudfront.net/img/270644/original/17D_0111.jpg',
    href: '/journal',
  },
  {
    id: 4,
    title: 'Water Supply Project',
    description:
      'The word “Gobi” in Mongolian is directly understood to mean a place with scarce water resources, as the region receives very little rainfall and consistently faces a shortage of clean drinking water, leading to serious hygiene issues, particularly among young children; to address this, a pilot project was implemented in Bayangovi soum, where a high-quality water source was identified near the settlement, and four pipelines were installed to supply drinking water—drawn from a depth of 70 meters—to distribution points in the soum center.',
    image:
      'https://d1f6qhhrbg3j8a.cloudfront.net/img/270646/original/ELBE2833.JPG',
    href: '/journal',
  },
];

export default function InsideJournalSlider() {
  const [activeIndex, setActiveIndex] = useState(4);
  const [visibleCount, setVisibleCount] = useState(4);
  const [isResetting, setIsResetting] = useState(false);
  const [selectedStory, setSelectedStory] = useState(null);
  const total = stories.length;

  const truncateWords = (text, maxWords = 50) => {
    const clean = String(text || "").replace(/\s+/g, " ").trim();
    const words = clean.split(" ").filter(Boolean);
    if (words.length <= maxWords) return clean;
    return `${words.slice(0, maxWords).join(" ")} ...`;
  };

  useEffect(() => {
    const setByViewport = () => {
      setVisibleCount(window.innerWidth < 768 ? 1 : 4);
    };
    setByViewport();
    window.addEventListener('resize', setByViewport);
    return () => window.removeEventListener('resize', setByViewport);
  }, []);

  useEffect(() => {
    // Reset track to real first block when layout changes.
    setActiveIndex(visibleCount);
    setIsResetting(true);
    const id = window.setTimeout(() => setIsResetting(false), 20);
    return () => window.clearTimeout(id);
  }, [visibleCount]);

  const extendedStories = useMemo(() => {
    const leftClones = stories.slice(-visibleCount);
    const rightClones = stories.slice(0, visibleCount);
    return [...leftClones, ...stories, ...rightClones];
  }, [visibleCount]);

  const handleTransitionEnd = () => {
    const minRealIndex = visibleCount;
    const maxRealIndex = visibleCount + total - 1;

    if (activeIndex > maxRealIndex) {
      const overflow = activeIndex - maxRealIndex - 1;
      setIsResetting(true);
      setActiveIndex(minRealIndex + overflow);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsResetting(false));
      });
    } else if (activeIndex < minRealIndex) {
      const underflow = minRealIndex - activeIndex - 1;
      setIsResetting(true);
      setActiveIndex(maxRealIndex - underflow);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsResetting(false));
      });
    }
  };

  const next = () => setActiveIndex((prev) => prev + 1);
  const prev = () => setActiveIndex((prev) => prev - 1);
  const openStoryModal = (story) => setSelectedStory(story);
  const closeStoryModal = () => setSelectedStory(null);

  useEffect(() => {
    if (!selectedStory) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeStoryModal();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [selectedStory]);

  return (
    <section className="w-full px-4 md:px-0 mt-12 md:mt-16 mb-12 md:mb-16">
      <div className="max-w-[1780px] mx-auto p-5 md:p-0 lg:p-0">
        <div className="flex items-center justify-center mb-5 md:mb-6">
          <h2 className="text-[28px] md:text-[34px] text-[#1e1c1a] text-center" style={headingFont}>
            Our journal
          </h2>
        </div>

        <div className="relative px-4 md:px-8 lg:px-10">
          <button
            type="button"
            onClick={prev}
            aria-label="Previous slide"
            className="absolute left-0 md:left-1 top-[42%] -translate-y-1/2 z-20 h-[100px] w-10 flex items-center justify-center text-[#3d372f] hover:text-[#1f1a16] transition-colors"
          >
            <svg viewBox="0 0 24 24" className="h-[72px] w-[28px]" fill="none" stroke="currentColor" aria-hidden>
              <path d="M15 4L8 12L15 20" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next slide"
            className="absolute right-0 md:right-1 top-[42%] -translate-y-1/2 z-20 h-[100px] w-10 flex items-center justify-center text-[#3d372f] hover:text-[#1f1a16] transition-colors"
          >
            <svg viewBox="0 0 24 24" className="h-[72px] w-[28px]" fill="none" stroke="currentColor" aria-hidden>
              <path d="M9 4L16 12L9 20" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className="overflow-hidden">
          <div
            className={`flex ${isResetting ? 'transition-none' : 'transition-transform duration-700 ease-out'}`}
            style={{ transform: `translateX(-${(activeIndex * 100) / visibleCount}%)` }}
            onTransitionEnd={handleTransitionEnd}
          >
            {extendedStories.map((story, i) => (
            <article key={`${story.id}-${i}`} className="flex flex-col px-4 md:px-5" style={{ flex: `0 0 ${100 / visibleCount}%` }}>
              <button
                type="button"
                onClick={() => openStoryModal(story)}
                className="block w-full overflow-hidden bg-[#ebe8e1] text-left"
              >
                <img
                  src={story.image}
                  alt={story.title}
                  className="w-full aspect-[3/4] object-cover hover:scale-[1.02] transition-transform duration-300"
                  loading="lazy"
                />
              </button>
              <div className="pt-4">
                <h3
                  className="text-[18px] lg:text-[24px] leading-[1.3] text-[#2c2721] mb-2"
                  style={headingFont}
                >
                  {story.title}
                </h3>
                <p
                  className="text-[14px] lg:text-[18px] leading-[1.5] text-[#5f574c]"
                  style={bodyFont}
                >
                  {truncateWords(story.description, 28)}
                </p>
              </div>
            </article>
          ))}
          </div>
          </div>
        </div>
      </div>

      {selectedStory && (
        <div
          className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4"
          onClick={closeStoryModal}
        >
          <div
            className="bg-white w-full max-w-[920px] max-h-[90vh] overflow-auto rounded-sm shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="relative">
              <img
                src={selectedStory.image}
                alt={selectedStory.title}
                className="w-full h-[260px] md:h-[360px] object-cover"
              />
              <button
                type="button"
                onClick={closeStoryModal}
                aria-label="Close details popup"
                className="absolute right-3 top-3 h-8 w-8 rounded-full bg-white/90 text-[#2c2520] hover:bg-white"
              >
                ✕
              </button>
            </div>
            <div className="p-5 md:p-7">
              <h3 className="text-[26px] md:text-[32px] text-[#1f1a16] mb-4" style={headingFont}>
                {selectedStory.title}
              </h3>
              <p className="text-[15px] lg:text-[18px] leading-7 text-[#544d44] whitespace-pre-line" style={bodyFont}>
                {selectedStory.description}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
